import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { submitQuickOrder } from '../api/orders';
import { Product } from '../api/products';
import { validateMoroccanPhone } from '../utils/phoneValidator';
import { useSettings } from '../context/SettingsContext';

interface InlineOrderFormProps {
    product: Product;
    variant?: { id: number; name: string; name_ar?: string; price: string; } | null;
    quantity: number;
}

const InlineOrderForm: React.FC<InlineOrderFormProps> = ({ product, variant, quantity }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { getSetting } = useSettings();
    const isRTL = i18n.dir() === 'rtl';

    const [formData, setFormData] = useState({ name: '', phone: '', city: '', address: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Calculate prices
    const unitPrice = variant ? parseFloat(variant.price) : (product.discount_price || product.price);
    const totalPrice = unitPrice * quantity;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (error) setError(null);
    };

    const validateForm = () => {
        if (!formData.name || !formData.phone || !formData.city || !formData.address) {
            setError(t('quickOrderForm.fillFormFirst', 'Please fill all fields first'));
            return false;
        }

        if (!validateMoroccanPhone(formData.phone)) {
            setError(t('validation.invalidPhone', 'Invalid phone number'));
            return false;
        }

        return true;
    };

    const handleDirectSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) return;

        setLoading(true);
        try {
            await submitQuickOrder({
                product_id: product.id,
                qty: quantity,
                product_variant_id: variant?.id,
                ...formData
            });
            setSuccess(true);
            setTimeout(() => {
                setSuccess(false);
                setFormData({ name: '', phone: '', city: '', address: '' });
                navigate('/thank_you');
            }, 1500);
        } catch (err) {
            setError(t('quickOrderForm.errorGeneric'));
        } finally {
            setLoading(false);
        }
    };

    const handleWhatsappSubmit = (e: React.MouseEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) return;

        const phoneNumber = '212660840732';
        const productName = isRTL ? product.title_ar : product.title_fr;
        const variantName = variant ? (isRTL ? (variant.name_ar || variant.name) : variant.name) : (isRTL ? (product.unit_name_ar || product.unit_name_fr) : product.unit_name_fr);

        const messageLines = [
            `🛍️ *Nouvelle Commande / طلب جديد*`,
            `------------------------`,
            `*Produit / المنتج:* ${productName}`,
            ...(variantName ? [`*Option / الخيار:* ${variantName}`] : []),
            `*Quantité / الكمية:* ${quantity}`,
            `*Prix Total / السعر الإجمالي:* ${totalPrice.toFixed(2)} ${product.currency}`,
            `------------------------`,
            `*Nom / الاسم:* ${formData.name}`,
            `*Téléphone / الهاتف:* ${formData.phone}`,
            `*Ville / المدينة:* ${formData.city}`,
            `*Adresse / العنوان:* ${formData.address}`
        ];

        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(messageLines.join('\n'))}`;
        window.open(whatsappUrl, '_blank');

        // Clear form after opening whatsapp to prevent double submission
        setFormData({ name: '', phone: '', city: '', address: '' });
    };

    if (success) {
        return (
            <div className="bg-white rounded-3xl p-8 shadow-soft text-center h-full flex flex-col items-center justify-center min-h-[400px]">
                <div className="relative mx-auto w-24 h-24 mb-8">
                    {/* Outer Ring Animation */}
                    <motion.div
                        className="absolute inset-0 border-4 border-primary/20 rounded-full"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1.2, opacity: 0 }}
                        transition={{ duration: 1, repeat: Infinity }}
                    />
                    {/* Success Icon Animation */}
                    <motion.div
                        className="relative flex items-center justify-center w-full h-full bg-primary/10 rounded-full"
                        initial={{ scale: 0, rotate: -45 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    >
                        <svg className="h-12 w-12 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <motion.path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="3"
                                d="M5 13l4 4L19 7"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            />
                        </svg>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {formData.name
                            ? t('quickOrderForm.processingName', { name: formData.name })
                            : t('quickOrderForm.processingGeneral')}
                    </h3>
                    <div className="flex justify-center gap-1 mt-4">
                        {[0, 1, 2].map((i) => (
                            <motion.div
                                key={i}
                                className="w-2 h-2 bg-primary rounded-full"
                                animate={{ scale: [1, 1.5, 1], opacity: [0.3, 1, 0.3] }}
                                transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }}
                            />
                        ))}
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div id="inline-order-form" className="relative bg-white rounded-[2rem] p-6 md:p-8 shadow-2xl border-4 border-primary/20 mb-8 mt-12 overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl pointer-events-none"></div>

            <div className="relative z-10">
                <div className="mb-8 text-center pt-2">
                    <h3 className="text-3xl md:text-4xl font-black text-dark mb-2 tracking-tight">
                        {t('common.quickOrder')}
                    </h3>
                    <p className="text-neutral-500 font-medium text-sm md:text-base">
                        {t('quickOrderForm.subtitle', 'Remplissez ce formulaire et payez à la livraison !')}
                    </p>
                </div>

                <form onSubmit={handleDirectSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('quickOrderForm.fullName')}
                            </label>
                            <input
                                type="text"
                                name="name"
                                id="name"
                                value={formData.name}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-primary focus:border-primary transition-all ${isRTL ? 'text-right' : 'text-left'}`}
                                placeholder={t('quickOrderForm.enterName')}
                            />
                        </div>

                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('quickOrderForm.phone')}
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                id="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-primary focus:border-primary transition-all`}
                                placeholder={t('quickOrderForm.phonePlaceholder')}
                                dir="ltr"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('quickOrderForm.city')}
                            </label>
                            <input
                                type="text"
                                name="city"
                                id="city"
                                value={formData.city}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-primary focus:border-primary transition-all ${isRTL ? 'text-right' : 'text-left'}`}
                                placeholder={t('quickOrderForm.cityPlaceholder')}
                            />
                        </div>

                        <div>
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('quickOrderForm.address')}
                            </label>
                            <input
                                type="text"
                                name="address"
                                id="address"
                                value={formData.address}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-primary focus:border-primary transition-all ${isRTL ? 'text-right' : 'text-left'}`}
                                placeholder={t('quickOrderForm.addressPlaceholder')}
                            />
                        </div>
                    </div>

                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                            className="text-red-500 text-sm font-medium bg-red-50 p-3 rounded-lg border border-red-100"
                        >
                            {error}
                        </motion.div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 pt-4">
                        <button
                            type="button"
                            onClick={handleWhatsappSubmit}
                            disabled={loading}
                            className="w-full inline-flex justify-center items-center gap-2 rounded-xl border border-transparent bg-[#25D366] hover:bg-[#128C7E] px-6 py-4 text-base font-bold text-white focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                            </svg>
                            {t('quickOrderForm.orderViaWhatsapp')}
                        </button>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full inline-flex justify-center items-center gap-2 rounded-xl border border-transparent bg-primary px-6 py-4 text-base font-bold text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                        >
                            {loading ? (
                                t('common.loading')
                            ) : (
                                <>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {t('common.quickOrder')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InlineOrderForm;
